// 📁 src/components/Card/ProfesionalCard.tsx
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CardsBlockCss } from "../../../styles";
import { PerfilProfesional, ProfessionalCardProps } from "@/types";
import { buildImageUrl } from "@/api/buildImageUrl";
import { safeFetch } from "@/api/safeFetch";
import Loader from "../loaderAndError/Loader";

// 💼 Componente principal
const ProfesionalCard: React.FC<ProfessionalCardProps> = ({ profesional, servicio }) => {
  const [perfilProfesional, setPerfilProfesional] = useState<PerfilProfesional | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  // 🔹 Fix visual
  const [, setRefreshKey] = useState(0);

  // 🚀 Fetch perfil
  useEffect(() => {
    let isMounted = true;

    const fetchPerfil = async () => {
      try {
        const res = await safeFetch(
          `/api/profesional/${servicio}/${profesional.perfil_profesional_id}`
        );

        if (!res.ok) {
          throw new Error("Error al cargar datos del profesional");
        }

        const data = await res.json();

        if (isMounted) {
          setPerfilProfesional({
            ...data.perfil,
            nombre_empresa: data.user.nombre_empresa,
          });

          setError(null);
        }
      } catch (err) {
        if (isMounted) {
          setError((err as Error).message);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
          requestAnimationFrame(() => setRefreshKey((k) => k + 1));
        }
      }
    };

    fetchPerfil();
    return () => {
      isMounted = false;
    };
  }, [profesional.profesional_id, profesional.perfil_profesional_id, servicio]);

  // ⏳ Loading
  if (loading)
    return (
      <div
        className={`${CardsBlockCss.profesion} rounded-2xl overflow-hidden w-full h-64 bg-gray-200 animate-pulse`}
      />
    );

  // ❌ Error
  if (error)
    return (
      <div className="w-full h-64 flex items-center justify-center text-red-500 border border-red-300 rounded-xl">
        Error al cargar
      </div>
    );

  // ⚠️ Sin datos
  if (!perfilProfesional)
    return (
      <div className="w-full h-64 flex items-center justify-center text-zinc-500 border rounded-xl">
        No hay datos
      </div>
    );

  // ✅ Render final
  const hasFoto = Boolean(perfilProfesional.foto_perfil);

  return (
    <div className={`${CardsBlockCss.profesion} rounded-2xl overflow-hidden min-h-48 relative`}>
      <Link
        to={`/profesional/${servicio}/${profesional.perfil_profesional_id}`}
        className="relative block w-full h-full"
      >
        {/* Loader */}
        {!imageLoaded && (
          <div className="absolute inset-0 flex justify-center items-center bg-gray-200 z-10">
            <Loader />
          </div>
        )}

        {/* Imagen / Logo adaptativo */}
        <div className="w-full h-full flex items-center justify-center bg-gray-100 overflow-hidden">
          <img
            src={hasFoto ? buildImageUrl(perfilProfesional.foto_perfil) : "/img/logo/ImagenLogo2.png"}
            alt={profesional.nombre_empresa}
            className={`transition-all duration-300 ease-in-out w-full h-full ${hasFoto ? "object-cover" : "object-contain"
              } ${imageLoaded ? "opacity-100" : "opacity-0"}`}
            onLoad={() => setImageLoaded(true)}
          />
        </div>

        {/* Nombre */}
        <div className={`${CardsBlockCss.nombreProfesion} p-2`}>
          <h2 className="text-sm md:text-[18px] font-semibold text-center">
            {perfilProfesional.nombre_comercial?.trim() || profesional.nombre_empresa}
          </h2>
        </div>

        {/* Descripción */}
        <div className={`${CardsBlockCss.descripcionProfesion} p-4`}>
          <p className="text-sm text-start leading-relaxed">
            {perfilProfesional.descripcion}
          </p>
        </div>
      </Link>
    </div>
  );
};

export default ProfesionalCard;
